import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import useFetchObject from '../../api/useFetchObject';
import LoadingScreen from '../../components/common/LoadingScreen';

const DirectorateView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: directorate, isLoading } = useFetchObject('directorates', 'directorates', id);
  const { data: province } = useFetchObject('provinces', 'provinces', directorate?.province_id);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!directorate) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('common.not_found')}
          </Typography>
          <Button
            component={RouterLink}
            to="/directorates"
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
          >
            {t('common.back_to_list')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          component={RouterLink}
          to="/directorates"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          {t('common.back')}
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {directorate.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {t('directorates.code')}: {directorate.code}
            </Typography>
          </Box>
          <Box>
            <Button
              component={RouterLink}
              to={`/directorates/${id}/edit`}
              variant="contained"
              startIcon={<EditIcon />}
            >
              {t('common.edit')}
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('directorates.basic_info')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('directorates.name')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography variant="body1">{directorate.name}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('directorates.code')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography variant="body1">{directorate.code}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('directorates.province')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  {province ? (
                    <Button
                      component={RouterLink}
                      to={`/provinces/${province.id}`}
                      color="primary"
                      sx={{ p: 0, textTransform: 'none' }}
                    >
                      {province.name}
                    </Button>
                  ) : (
                    <Typography variant="body1">—</Typography>
                  )}
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common.status')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Chip
                    size="small"
                    label={directorate.is_active ? t('common.active') : t('common.inactive')}
                    color={directorate.is_active ? 'success' : 'default'}
                  />
                </Grid>
                
                {directorate.description && (
                  <>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('directorates.description')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Typography variant="body1">{directorate.description}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('directorates.contact_info')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                {directorate.address && (
                  <>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('directorates.address')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body1">{directorate.address}</Typography>
                      </Box>
                    </Grid>
                  </>
                )}
                
                {directorate.phone && (
                  <>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('directorates.phone')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body1">{directorate.phone}</Typography>
                      </Box>
                    </Grid>
                  </>
                )}
                
                {directorate.email && (
                  <>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('directorates.email')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          <a href={`mailto:${directorate.email}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            {directorate.email}
                          </a>
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('common.multilingual_information')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                {directorate.name_dari && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('directorates.name_dari')}
                    </Typography>
                    <Typography variant="body1" dir="rtl" lang="fa">
                      {directorate.name_dari}
                    </Typography>
                  </Box>
                )}
                
                {directorate.name_pashto && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('directorates.name_pashto')}
                    </Typography>
                    <Typography variant="body1" dir="rtl" lang="ps">
                      {directorate.name_pashto}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
          
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('common.metadata')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                {directorate.created_at && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('common.created_at')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        {new Date(directorate.created_at).toLocaleString()}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                {directorate.updated_at && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('common.updated_at')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        {new Date(directorate.updated_at).toLocaleString()}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DirectorateView; 